import React from "react";
import axios from "axios";
import Select from "react-select";
import Table from "react-bootstrap/Table";
import Order from "./Order";
import loading from "./assets/loading.gif";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";

import "./Orders.css";
import "font-awesome/css/font-awesome.min.css";

const PER_PAGE = 200;

const STATUS_FILTERS = [
  ["active", "Active"],
  ["in_stock", "In stock"],
  ["at_warehouse", "At warehouse"],
  ["shipped", "Shipped"],
  ["archived", "Archived"],
  ["all", "All"],
];

const CREATED_FILTERS = [
  ["1w", "1W"],
  ["1m", "1M"],
  ["6m", "6M"],
  ["1y", "1Y"],
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

class Orders extends React.Component {
  constructor(props) {
    super(props);

    this.searchRef = React.createRef();
    this.headerRef = React.createRef();

    this.state = {
      loading: true,
      orders: [],
      carriers: [],
      warehouseOptions: [],
      warehouse: this.getQueryParam("warehouse") || "all", // warehouse: { value: 'champion', label: 'Champion'},
      sort: this.getQueryParam("sort")?.split("/")?.[0] || "cust_ref",
      sortDirection: this.getQueryParam("sort")?.split("/")?.[1] || "desc",
      filter: this.getQueryParam("filter") || "active",
      saved: false,
      exported: false,
      page:
        (this.getQueryParam("page") && parseInt(this.getQueryParam("page"))) ||
        1,
      searchInput: this.getQueryParam("search") || "",
      isAllocationMode: this.getQueryParam("allocation"),
      headerBottom: 0,
    };
  }

  async fetchOrders() {
    this.setState({ loading: true });

    const orders = (
      await axios.get(
        `/api/orders${window.location.search}&page=${this.state.page}`
      )
    ).data;

    this.setState({ loading: false });

    return orders;
  }

  getWarehouseOption(id) {
    return this.state.warehouseOptions.find((option) => option.value === id);
  }

  async componentDidMount() {
    this.setDefaultParams();
    document.addEventListener("keydown", this.focusSearchOnSlash);

    // the header is fixed and wraps on narrow screens, so the table has to be
    // pushed down to wherever it actually ends
    this.headerObserver = new ResizeObserver(([entry]) =>
      this.setState({
        headerBottom: entry.target.offsetTop + entry.target.offsetHeight,
      })
    );
    this.headerObserver.observe(this.headerRef.current);

    const warehouseOptions = (await axios.get("/api/warehouses")).data.map(
      (warehouse) => {
        return {
          value: warehouse.external_id,
          label:
            warehouse.external_id === "all" ? "All warehouses" : warehouse.name,
        };
      }
    );

    const orderStatusesOptions = (await axios.get("/api/order_statuses")).data;
    const carriers = (await axios.get("/api/carriers")).data;

    this.setState({ loading: true });

    this.setState({
      warehouseOptions,
      orderStatusesOptions,
      orders: await this.fetchOrders(),
      carriers,
    });
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.focusSearchOnSlash);
    this.headerObserver.disconnect();
  }

  focusSearchOnSlash = (e) => {
    const tag = e.target.tagName;
    if (
      e.key !== "/" ||
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      e.target.isContentEditable
    )
      return;

    e.preventDefault();
    this.searchRef.current?.focus();
  };

  setDefaultParams() {
    if (!this.getQueryParam("warehouse"))
      this.insertQueryParam("warehouse", "all");
    if (!this.getQueryParam("sort"))
      this.insertQueryParam("sort", "cust_ref/desc");
    if (!this.getQueryParam("filter"))
      this.insertQueryParam("filter", "active");
    if (!this.getQueryParam("page")) this.insertQueryParam("page", "1");
  }

  insertQueryParam(key, value) {
    if (window.history.pushState) {
      let searchParams = new URLSearchParams(window.location.search);
      searchParams.set(key, value);
      let newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        searchParams.toString();
      window.history.pushState({ path: newurl }, "", newurl);
    }
  }
  deleteQueryParam(key) {
    let searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(key);
    let newurl =
      window.location.protocol +
      "//" +
      window.location.host +
      window.location.pathname +
      "?" +
      searchParams.toString();
    window.history.pushState({ path: newurl }, "", newurl);
  }
  getQueryParam(name, url = window.location.href) {
    name = name.replace(/[[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  setWarehouse = async (selection) => {
    this.insertQueryParam("warehouse", selection.value);
    this.setState({
      warehouse: selection.value,
      orders: await this.fetchOrders(),
    });
  };

  selectWarehouse = async (idx, warehouseExternalId) => {
    await axios.post(
      `/api/orders/${this.state.orders[idx].cust_ref.replace(
        "#",
        ""
      )}/update_warehouse`,
      { warehouse_external_id: warehouseExternalId }
    );

    const orders = (
      await axios.get(
        `/api/orders${window.location.search}&page=${this.state.page}`
      )
    ).data;

    orders[idx].shiphawk_rate_id = null;
    orders[idx].rates = null;

    this.setState({ orders });
  };

  fetchRates = async (idx) => {
    const orders = this.state.orders;
    // orders[idx].dirty = true; not persisting anything besides rates refresh in backend

    const updatedRates = (
      await axios.post(
        `/api/orders/${orders[idx].cust_ref.replace("#", "")}/fetch_rates`,
        orders[idx]
      )
    ).data;
    orders[idx].rates = updatedRates;

    this.setState({ orders });
  };

  sendOrderToShiphawk = async (idx) => {
    const orders = this.state.orders;

    await axios.post(
      `/api/orders/${orders[idx].cust_ref.replace("#", "")}/shiphawk`
    );
  };

  saveBomOverrides = async (idx) => {
    try {
      await axios.post(
        `/api/orders/${this.state.orders[idx].cust_ref.replace(
          "#",
          ""
        )}/update_bom_override`,
        { boms: this.state.orders[idx].boms }
      );
    } catch (e) {
      toast.error(e.response.data.error);
    }

    const orders = (
      await axios.get(
        `/api/orders${window.location.search}&page=${this.state.page}`
      )
    ).data;

    orders[idx].shiphawk_rate_id = null;
    orders[idx].rates = null;

    this.setState({ orders });
  };

  selectCarrier = (idx, carrier) => {
    const orders = this.state.orders;
    orders[idx].carrier = carrier;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  selectRate = (idx, rate) => {
    const orders = this.state.orders;
    orders[idx].rate = rate;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  toggleSend = (idx, e) => {
    if (e.target.checked) {
      const orders = this.state.orders;
      orders[idx].order_status = this.state.orderStatusesOptions.find(
        (status) => status.name === "Sent to Shiphawk"
      );
      orders[idx].dirty = true;

      this.setState({ orders });
    }
  };

  toggleFilter = async (filter) => {
    this.insertQueryParam("filter", filter);
    this.insertQueryParam("page", "1");

    await this.setState({
      page: 1,
      filter,
    });

    this.setState({
      orders: await this.fetchOrders(),
    });
  };

  toggleSort = async (newSort) => {
    let sort = this.state.sort;
    let sortDirection = this.state.sortDirection;

    if (newSort === sort) {
      if (sortDirection === "asc") {
        sortDirection = "desc";
      } else {
        sortDirection = "asc";
      }
    } else {
      sort = newSort;
      sortDirection = "desc";
    }

    this.insertQueryParam("sort", `${sort}/${sortDirection}`);

    this.setState({
      orders: await this.fetchOrders(),
      sort,
      sortDirection,
    });
  };

  selectOrderStatus = (idx, orderStatus) => {
    const orders = this.state.orders;
    orders[idx].order_status = orderStatus;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  updateDescription = (idx, e) => {
    const orders = this.state.orders;
    orders[idx].description = e.target.value;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  updateCombineWithOrderNumber = (idx, e) => {
    const orders = this.state.orders;
    orders[idx].combine_with = e.target.value;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  updateCustomerNotes = (idx, e) => {
    const orders = this.state.orders;
    orders[idx].customer_notes = e.target.value;
    orders[idx].dirty = true;

    this.setState({ orders });
  };

  // boms allocate in the order the ids are sent, so reordering here is what
  // drives the allocation sequence on the next fetch
  reallocate = async (orders) => {
    if (!orders.length) {
      await this.exitAllocationMode();
      return;
    }

    this.insertQueryParam("allocation", orders.map((o) => o.id).toString());

    this.setState({
      orders: await this.fetchOrders(),
      isAllocationMode: true,
    });
  };

  ignoreOrder = async (idx, e) => {
    const orders = [...this.state.orders];
    orders.splice(idx, 1);

    await this.reallocate(orders);
  };

  moveOrderUp = async (idx, e) => {
    if (idx <= 0) return;

    const orders = [...this.state.orders];
    swapElements(orders, idx - 1, idx);

    await this.reallocate(orders);
  };

  moveOrderDown = async (idx, e) => {
    const orders = [...this.state.orders];
    if (idx >= orders.length - 1) return;

    swapElements(orders, idx, idx + 1);

    await this.reallocate(orders);
  };

  moveOrderTop = async (idx, e) => {
    if (idx <= 0) return;

    const orders = [...this.state.orders];
    const [moved] = orders.splice(idx, 1);
    orders.unshift(moved);

    await this.reallocate(orders);
  };

  moveOrderBottom = async (idx, e) => {
    const orders = [...this.state.orders];
    if (idx >= orders.length - 1) return;

    const [moved] = orders.splice(idx, 1);
    orders.push(moved);

    await this.reallocate(orders);
  };

  enterAllocationMode = async (idx, e) => {
    await this.reallocate([...this.state.orders]);
  };

  exitAllocationMode = async (idx, e) => {
    this.deleteQueryParam("allocation");

    this.setState({
      orders: await this.fetchOrders(),
      isAllocationMode: false,
    });
  };

  saveOrders = async () => {
    const dirtyOrders = this.state.orders
      .filter((order) => order.dirty || order.has_bom_override)
      .map((order) =>
        (({
          id,
          order_status,
          priority,
          description,
          combine_with,
          boms,
          has_bom_override,
          customer_notes,
          warehouse,
          carrier,
          rate,
        }) => ({
          id,
          order_status,
          priority,
          description,
          combine_with,
          boms,
          has_bom_override,
          customer_notes,
          warehouse,
          carrier,
          rate,
        }))(order)
      );

    try {
      await axios.put("/api/orders/bulk", { orders: dirtyOrders });
    } catch (e) {
      toast.error(e.message);
    }

    toast.success("Orders saved successfully!");
    this.setState({ saved: true });

    // Reset dirty orders
    const orders = this.state.orders;
    orders.forEach((order) => {
      order.dirty = false;
      order.has_bom_override = false;
    });

    this.setState({ orders });

    setTimeout(() => this.setState({ saved: false }), 3000);
  };

  exportOrders = async () => {
    const resp = await axios.get(
      `/api/orders/export?filter=${this.state.filter}`,
      { responseType: "arraybuffer" }
    );

    this.setState({ exported: true });

    setTimeout(() => this.setState({ exported: false }), 3000);
  };

  searchOrder = async () => {
    this.insertQueryParam("search", this.state.searchInput);
    this.setState({
      orders: await this.fetchOrders(),
    });
  };

  changePage = async (next) => {
    await this.setState({ page: this.state.page + next });
    this.insertQueryParam("page", this.state.page);

    this.setState({ orders: await this.fetchOrders() });
  };

  renderPager() {
    return (
      <div className="orders-pager">
        <button
          className="orders-pager-btn"
          disabled={this.state.page <= 1}
          onClick={() => this.changePage(-1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span>{this.state.page}</span>
        <button
          className="orders-pager-btn"
          disabled={this.state.orders.length < PER_PAGE}
          onClick={() => this.changePage(1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    );
  }

  render() {
    const unsavedCount = this.state.orders.filter(
      (order) => order.dirty || order.has_bom_override
    ).length;

    return (
      <div className="orders-container">
        <div className="orders-header" ref={this.headerRef}>
          <div className="orders-page-head">
            <div className="orders-heading">
              <h1 className="orders-title">Orders</h1>
              <div className="orders-subtitle">
                Page {this.state.page} · {this.state.orders.length} orders ·{" "}
                {PER_PAGE} per page
              </div>
              <button
                className="orders-btn orders-btn-sm"
                title="Emails a CSV of the current filter"
                onClick={this.exportOrders}
              >
                <i className="fa fa-download" aria-hidden="true"></i>
                {this.state.exported ? "Emailing!" : "Export"}
              </button>
            </div>
            <div className="orders-actions">
              {unsavedCount > 0 && (
                <span className="orders-unsaved">
                  <span className="orders-unsaved-dot" />
                  Unsaved changes
                </span>
              )}
              <button
                className="orders-btn orders-btn-primary"
                onClick={() => this.saveOrders()}
              >
                {this.state.saved ? "Saved" : "Save"}
                {unsavedCount > 0 && (
                  <span className="orders-btn-count">{unsavedCount}</span>
                )}
              </button>
            </div>
          </div>

          <div className="orders-toolbar">
            <div className="orders-segmented">
              {STATUS_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  className={classNames(
                    this.state.filter === value && "orders-segmented-on"
                  )}
                  onClick={() => this.toggleFilter(value)}
                >
                  {label}
                </button>
              ))}
              <span className="orders-segmented-divider" />
              <span className="orders-segmented-label">Created</span>
              {CREATED_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  className={classNames(
                    this.state.filter === value && "orders-segmented-on"
                  )}
                  onClick={() => this.toggleFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <Select
              className="orders-warehouse"
              classNamePrefix="orders-warehouse"
              options={this.state.warehouseOptions}
              onChange={this.setWarehouse}
              value={this.getWarehouseOption(this.state.warehouse)}
              isSearchable={false}
            />

            <label className="orders-search">
              <i className="fa fa-search" aria-hidden="true"></i>
              <input
                ref={this.searchRef}
                placeholder="Find order #"
                value={this.state.searchInput}
                onChange={(e) => this.setState({ searchInput: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    this.searchOrder();
                  }
                }}
              />
              <kbd>/</kbd>
            </label>

            {!this.state.isAllocationMode && (
              <div className="orders-toolbar-end">
                <button
                  className="orders-link-btn"
                  title="Manually sequence orders to see how inventory allocates"
                  onClick={() => this.enterAllocationMode()}
                >
                  <i className="fa fa-sort" aria-hidden="true"></i>
                  Allocation mode
                </button>
                {this.renderPager()}
              </div>
            )}
          </div>

          {this.state.isAllocationMode && (
            <div className="orders-allocation-banner">
              <i className="fa fa-sort" aria-hidden="true"></i>
              <span>
                <b>Allocation mode.</b> Reorder rows to decide which orders get
                stock first.
              </span>
              <button
                className="orders-btn"
                onClick={() => this.exitAllocationMode()}
              >
                Exit allocation mode
              </button>
            </div>
          )}
        </div>

        {!this.state.loading && (
          <div
            className="orders-content"
            style={{ marginTop: this.state.headerBottom }}
          >
            <Table
              striped
              bordered
              hover
              className={classNames(
                this.state.isAllocationMode ? "orders-allocation-mode" : "",
                "orders-table"
              )}
            >
              <thead className="orders-table-list-header">
                <tr>
                  {this.state.isAllocationMode && <th>Ignore</th>}
                  {this.state.isAllocationMode && <th>Move</th>}
                  {this.state.isAllocationMode && <th>Combine with</th>}
                  <th
                    className="orders-sortable-header"
                    onClick={() => this.toggleSort("cust_ref")}
                  >
                    Order #
                    {this.state.sort === "cust_ref" &&
                      this.state.sortDirection === "desc" && (
                        <i className="fa fa-sort-desc" aria-hidden="true"></i>
                      )}
                    {this.state.sort === "cust_ref" &&
                      this.state.sortDirection === "asc" && (
                        <i className="fa fa-sort-asc" aria-hidden="true"></i>
                      )}
                  </th>
                  <th>Order Date</th>
                  <th
                    className="orders-sortable-header"
                    onClick={() => this.toggleSort("order_status_id")}
                  >
                    Status
                    {this.state.sort === "order_status_id" &&
                      this.state.sortDirection === "desc" && (
                        <i className="fa fa-sort-desc" aria-hidden="true"></i>
                      )}
                    {this.state.sort === "order_status_id" &&
                      this.state.sortDirection === "asc" && (
                        <i className="fa fa-sort-asc" aria-hidden="true"></i>
                      )}
                  </th>
                  <th className="order-sku">Order Items</th>
                  <th className="order-sku">BOMs</th>
                  {/* <th className="order-sku">New Subs</th> */}
                  <th>Warehouse</th>
                  {/* <th>Winning Carrier</th> */}
                  {/* {!this.state.isAllocationMode && <th>Rates</th> */}

                  {!this.state.isAllocationMode && (
                    <th className="orders-configuration">Shipping</th>
                  )}

                  {!this.state.isAllocationMode && <th>Send to Shiphawk</th>}
                  {/* <th>Shipping Country</th> */}
                  {!this.state.isAllocationMode && <th>Warehouse Status</th>}
                  <th>Comments</th>
                  <th>Customer Notes</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody className="orders-table-list">
                {this.state.orders.map((order, idx) => {
                  return (
                    <Order
                      key={order.id}
                      idx={idx}
                      order={order}
                      orderStatusesOptions={this.state.orderStatusesOptions}
                      carriers={this.state.carriers}
                      isAllocationMode={this.state.isAllocationMode}
                      selectWarehouse={(idx, warehouse) =>
                        this.selectWarehouse(idx, warehouse)
                      }
                      fetchRates={(idx) => this.fetchRates(idx)}
                      sendOrderToShiphawk={(idx) =>
                        this.sendOrderToShiphawk(idx)
                      }
                      selectCarrier={(idx, carrier) =>
                        this.selectCarrier(idx, carrier)
                      }
                      selectRate={(idx, rate) => this.selectRate(idx, rate)}
                      toggleSend={(idx, e) => this.toggleSend(idx, e)}
                      selectOrderStatus={(idx, orderStatus) =>
                        this.selectOrderStatus(idx, orderStatus)
                      }
                      updateDescription={(idx, e) =>
                        this.updateDescription(idx, e)
                      }
                      updateCombineWithOrderNumber={(idx, e) =>
                        this.updateCombineWithOrderNumber(idx, e)
                      }
                      updateCustomerNotes={(idx, e) =>
                        this.updateCustomerNotes(idx, e)
                      }
                      saveBomOverrides={(idx) => this.saveBomOverrides(idx)}
                      ignoreOrder={(idx, e) => this.ignoreOrder(idx, e)}
                      moveOrderUp={(idx, e) => this.moveOrderUp(idx, e)}
                      moveOrderDown={(idx, e) => this.moveOrderDown(idx, e)}
                      moveOrderTop={(idx, e) => this.moveOrderTop(idx, e)}
                      moveOrderBottom={(idx, e) => this.moveOrderBottom(idx, e)}
                    />
                  );
                })}
              </tbody>
            </Table>

            <div className="orders-footer">
              {this.state.isAllocationMode ? (
                <button
                  className="orders-btn"
                  onClick={() => this.exitAllocationMode()}
                >
                  Exit allocation mode
                </button>
              ) : (
                this.renderPager()
              )}
            </div>
          </div>
        )}

        {this.state.loading && (
          <div className="loading-container">
            <div className="loading-title">Loading...</div>
            <img className="loading" src={loading} alt="" />
          </div>
        )}

        <ToastContainer
          position="top-center"
          autoClose={10000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    );
  }
}

const swapElements = (array, index1, index2) => {
  [array[index1], array[index2]] = [array[index2], array[index1]];
};

export default Orders;
